/**
 * BUSINESS FORM — Modale per creare o modificare un'azienda.
 *
 * Thin wrapper Dialog (stesse classi .app-dialog/.tc-head del form contatto)
 * + BusinessFormBody + logica thunk. Stesso pattern di PersonForm: azienda e
 * contatto si aprono dagli stessi punti — scheda di dettaglio e home — e
 * devono essere la stessa maschera, non una modale e un drawer.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchBusiness, createBusiness, updateBusiness } from './businessThunks';
import { BusinessFormBody } from './BusinessFormBody';
import { type BusinessCreatePayload, BUSINESS_FORM_DEFAULTS } from './businessTypes';
import { addEmailAddress, updateEmailAddress } from '../emailAddress/emailAddressThunks';
import { addPhone, updatePhone } from '../phone/phoneThunks';
import { addAddress, updateAddress } from '../address/addressThunks';
import { addressText } from '../address/addressTypes';
import { useToast } from '../../components/useToast';

interface BusinessFormProps {
  onClose: () => void;
  /** Chiamata dopo il salvataggio; in create riceve l'id del Business creato. */
  onSaved?: (businessId?: number) => void;
  /** Se presente, la modale entra in modalità edit (fetch + update). */
  businessId?: number | null;
  /** Solo in create: precompila i campi indicati (merge su DEFAULT_VALUES),
   *  es. da dati estratti da un messaggio. DEVE avere reference stabile
   *  (memoizzato dal chiamante): un nuovo oggetto ad ogni render farebbe
   *  ripartire il reset del form nel body sovrascrivendo l'input utente. */
  prefillValues?: Partial<BusinessCreatePayload>;
}

const FORM_ID = 'business-form';

export default function BusinessForm({ onClose, onSaved, businessId, prefillValues }: BusinessFormProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();
  const isEdit = businessId != null;
  const [submitting, setSubmitting] = useState(false);
  const { currentItem } = useAppSelector((state) => state.businesses);

  // Nessuna pulizia di `currentItem` allo smontaggio: quando questo form si apre
  // DENTRO BusinessDetail è lo stesso slot che la scheda sta mostrando, e
  // azzerarlo alla chiusura la lasciava appesa a "Caricamento...". Il rischio che
  // la clear copriva — prefill con l'azienda sbagliata rimasta in store — lo copre
  // ora il controllo sull'id in `initialValues`.
  useEffect(() => {
    if (isEdit && businessId) dispatch(fetchBusiness(businessId));
  }, [isEdit, businessId, dispatch]);

  // I campi email/phone/address sono prefillati col record PRIMARIO esistente
  // (find primary, fallback al primo); al submit vengono aggiornati (vedi
  // handleSubmit). Memoizzato sul reference di currentItem: senza useMemo un
  // qualsiasi re-render del parent genererebbe un nuovo oggetto e il useEffect
  // nel body sovrascriverebbe l'input dell'utente con i valori remoti.
  const initialValues = useMemo<BusinessCreatePayload | undefined>(() => {
    // Create: di norma nessun prefill (undefined -> BUSINESS_FORM_DEFAULTS nel body),
    // ma se arriva `prefillValues` precompiliamo i campi indicati.
    if (!isEdit) {
      return prefillValues ? { ...BUSINESS_FORM_DEFAULTS, ...prefillValues } : undefined;
    }
    // L'id deve corrispondere: in store può esserci ancora un'altra azienda.
    if (!currentItem || currentItem.businessId !== businessId) return undefined;
    const primaryEmail =
      currentItem.emailAddresses?.find((e) => e.primary) ?? currentItem.emailAddresses?.[0];
    const primaryPhone =
      currentItem.phones?.find((p) => p.primary) ?? currentItem.phones?.[0];
    const primaryAddress =
      currentItem.addresses?.find((a) => a.primary) ?? currentItem.addresses?.[0];
    return {
      externalCode: currentItem.externalCode ?? '',
      name: currentItem.name, domain: currentItem.domain,
      owner: currentItem.owner ?? '', sector: currentItem.sector ?? '',
      type: currentItem.type ?? '', employees: currentItem.employees ?? '',
      vatNumber: currentItem.vatNumber ?? '', fiscalCode: currentItem.fiscalCode ?? '',
      sdiCode: currentItem.sdiCode ?? '', pecEmail: currentItem.pecEmail ?? '',
      email: primaryEmail?.address ?? '', phone: primaryPhone?.number ?? '',
      addressName: primaryAddress?.name ?? '',
      addressDescription: primaryAddress?.description ?? '',
      // Il «-» salvato dal core al posto dei campi mancanti è un campo vuoto (FILO-52); lasciato vuoto non
      // conta come modifica (textChanged) e il core lo rimette.
      addressStreet: addressText(primaryAddress?.street),
      addressCity: addressText(primaryAddress?.city),
      addressZip: addressText(primaryAddress?.zip),
      addressCountryId: primaryAddress?.countryId ?? null,
      addressRegionId: primaryAddress?.regionId ?? null,
    };
  }, [isEdit, currentItem, prefillValues, businessId]);

  async function handleSubmit({
    email, phone,
    addressName, addressDescription, addressStreet, addressCity, addressZip,
    addressCountryId, addressRegionId,
    ...businessPayload
  }: BusinessCreatePayload) {
    setSubmitting(true);
    try {
      // 1. Create / update del Business (campi scalari). Errore qui e'
      // BLOCCANTE: toast d'errore, form resta aperta cosi' l'utente puo'
      // correggere (es. domain duplicato → 409 dal backend).
      let resultBusinessId: number;
      try {
        if (isEdit && businessId) {
          await dispatch(updateBusiness({ ...businessPayload, businessId })).unwrap();
          resultBusinessId = businessId;
        } else {
          const created = await dispatch(createBusiness(businessPayload)).unwrap();
          resultBusinessId = created.businessId;
        }
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        return;
      }

      // 2. Email/telefono PRIMARIO + address (best-effort): errori in toast ma
      // NON rollbacciano il Business (già creato/aggiornato).
      //  - CREATE, o EDIT senza primario esistente: add del valore come primario.
      //  - EDIT con primario esistente: update di quel record se il valore è
      //    cambiato; campo vuoto = nessuna modifica (rimozione inline dal
      //    dettaglio). La lista completa resta gestita da BusinessDetail.
      const trimmedEmail = email?.trim() ?? '';
      const trimmedPhone = phone?.trim() ?? '';

      const primaryEmail = isEdit
        ? currentItem?.emailAddresses?.find((e) => e.primary) ?? currentItem?.emailAddresses?.[0]
        : undefined;
      const primaryPhone = isEdit
        ? currentItem?.phones?.find((p) => p.primary) ?? currentItem?.phones?.[0]
        : undefined;

      if (trimmedEmail) {
        try {
          if (primaryEmail) {
            if (trimmedEmail !== primaryEmail.address) {
              await dispatch(updateEmailAddress({
                className: 'Business', classPK: resultBusinessId,
                emailAddressId: primaryEmail.emailAddressId,
                address: trimmedEmail, primary: true,
              })).unwrap();
            }
          } else {
            await dispatch(addEmailAddress({
              className: 'Business', classPK: resultBusinessId,
              address: trimmedEmail, primary: true,
            })).unwrap();
          }
        } catch (err) {
          toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      }
      if (trimmedPhone) {
        try {
          if (primaryPhone) {
            if (trimmedPhone !== primaryPhone.number) {
              await dispatch(updatePhone({
                className: 'Business', classPK: resultBusinessId,
                phoneId: primaryPhone.phoneId,
                number: trimmedPhone, primary: true,
              })).unwrap();
            }
          } else {
            await dispatch(addPhone({
              className: 'Business', classPK: resultBusinessId,
              number: trimmedPhone, primary: true,
            })).unwrap();
          }
        } catch (err) {
          toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      }
      // Address PRIMARIO.
      //  - Paese/Regione: inviati come valori grezzi (null incluso), così il
      //    cambio Paese azzera davvero la Regione lato server (C1).
      //  - Campi testo: inviati solo se valorizzati; svuotarli dal drawer =
      //    invariati (la cancellazione fine resta inline nel dettaglio).
      //  - EDIT: update solo se qualcosa è effettivamente cambiato (S1).
      //  - CREATE / nessun primario: add solo se c'è almeno un campo
      //    valorizzato (niente entry "fantasma").
      const formAddr = {
        name: addressName?.trim() || '',
        description: addressDescription?.trim() || '',
        street: addressStreet?.trim() || '',
        city: addressCity?.trim() || '',
        zip: addressZip?.trim() || '',
        countryId: addressCountryId ?? null,
        regionId: addressRegionId ?? null,
      };
      const hasAnyAddrField =
        !!formAddr.name || !!formAddr.description || !!formAddr.street ||
        !!formAddr.city || !!formAddr.zip ||
        formAddr.countryId != null || formAddr.regionId != null;
      // Body PATCH/POST: testo undefined-per-vuoto (non cancellabile), Paese/
      // Regione grezzi (null = azzera).
      const addrBody = {
        name: formAddr.name || undefined,
        description: formAddr.description || undefined,
        street: formAddr.street || undefined,
        city: formAddr.city || undefined,
        zip: formAddr.zip || undefined,
        countryId: formAddr.countryId,
        regionId: formAddr.regionId,
      };
      const primaryAddress = isEdit
        ? currentItem?.addresses?.find((a) => a.primary) ?? currentItem?.addresses?.[0]
        : undefined;
      if (primaryAddress) {
        // "changed" coerente col body: per il testo conta solo un nuovo valore
        // non vuoto e diverso (svuotare non si applica); Paese/Regione contano
        // qualsiasi differenza, null incluso.
        const textChanged = (next: string, cur: string | undefined) =>
          !!next && next !== (cur ?? '');
        const changed =
          textChanged(formAddr.name, primaryAddress.name) ||
          textChanged(formAddr.description, primaryAddress.description) ||
          textChanged(formAddr.street, primaryAddress.street) ||
          textChanged(formAddr.city, primaryAddress.city) ||
          textChanged(formAddr.zip, primaryAddress.zip) ||
          formAddr.countryId !== (primaryAddress.countryId ?? null) ||
          formAddr.regionId !== (primaryAddress.regionId ?? null);
        if (changed) {
          try {
            await dispatch(updateAddress({
              businessId: resultBusinessId,
              addressId: primaryAddress.addressId,
              ...addrBody,
              primary: true,
            })).unwrap();
          } catch (err) {
            toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
          }
        }
      } else if (hasAnyAddrField) {
        try {
          await dispatch(addAddress({
            businessId: resultBusinessId,
            ...addrBody,
            primary: true,
          })).unwrap();
        } catch (err) {
          toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      }

      toast({ severity: 'success', message: t('common.saved') });
      onSaved?.(resultBusinessId);
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  const footer = (
    <>
      <Button type="button" onClick={onClose} variant="outlined">
        {t('common.cancel')}
      </Button>
      <Button type="submit" form={FORM_ID} variant="contained" disabled={submitting}>
        {isEdit ? t('common.save') : t('common.create')}
      </Button>
    </>
  );

  // Stesse classi del form contatto (.app-dialog/.tc-head): le due maschere si
  // aprono dagli stessi punti e devono essere lo stesso oggetto.
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="tc-head">
        <span className="tc-kicker">
          {isEdit ? t('business.editBusiness') : t('business.createBusiness')}
        </span>
        <IconButton aria-label={t('common.cancel')} onClick={onClose} size="small" className="tc-x">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <BusinessFormBody
          formId={FORM_ID}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        />
      </DialogContent>
      <DialogActions className="app-dialog__footer">{footer}</DialogActions>
    </Dialog>
  );
}
